import { FC, Fragment, RefObject } from "react";

import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { FormProvider, UseFormReturn, useWatch } from "react-hook-form";

import { FormStepContainer } from "../FormStepContainer";

import { PrizeList } from "./helpers/usePrizeList";
import { PrizeValueRestriction, StepPrizesFormData } from "./helpers/types";
import { PrizeCard } from "./PrizeCard/PrizeCard";
import { PrizeValueLimitDialog } from "./PrizeValueLimitDialog/PrizeValueLimitDialog";

import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useTranslate } from "@/common/hooks/useTranslate";
import { ImageUploaderHandle } from "@/components/ImageUploader/types";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { AddDivider } from "@/features/LiveFormEditor/Steps/components/AddDivider";
import { Add } from "@/icons/outlined";

interface StepPrizesProps {
  form: UseFormReturn<StepPrizesFormData>;
  prizeList: PrizeList;
  onNext: () => Promise<void>;
  onBack: () => Promise<void>;
  valueRestriction: PrizeValueRestriction | null;
  onEditPrizes: () => void;
  onBackToDashboard: () => void;
  onUploaderChange?: (prizeId: string, uploaderRef: RefObject<ImageUploaderHandle | null> | null) => void;
  onPrizeActivate?: (prizeId: string) => void;
  isSweepstakes?: boolean;
}

export const StepPrizes: FC<StepPrizesProps> = ({
  form,
  prizeList,
  onNext,
  onBack,
  valueRestriction,
  onEditPrizes,
  onBackToDashboard,
  onUploaderChange,
  onPrizeActivate,
  isSweepstakes = false,
}) => {
  const { t } = useTranslate();
  const { isSmallScreen } = useMediaQuery();
  const isImageUploading = useWatch({ control: form.control, name: "isImageUploading" });
  const { prizes, expandedPrizeId, toggleExpanded, addPrize, duplicatePrize, deletePrize, movePrizeUp, movePrizeDown } =
    prizeList;

  const list = (
    <Stack>
      {prizes.map((prize, index) => (
        <Fragment key={prize.fieldKey}>
          <PrizeCard
            index={index}
            prizeId={prize.id}
            totalCardNumber={prizes.length}
            isExpanded={expandedPrizeId === prize.id}
            onToggleExpanded={toggleExpanded}
            onDelete={deletePrize}
            onDuplicate={duplicatePrize}
            onMoveUp={movePrizeUp}
            onMoveDown={movePrizeDown}
            onUploaderChange={onUploaderChange}
            onActivate={onPrizeActivate}
            isSweepstakes={isSweepstakes}
          />
          {index < prizes.length - 1 && <AddDivider onClick={() => addPrize(index + 1)} />}
        </Fragment>
      ))}
    </Stack>
  );

  return (
    <FormProvider {...form}>
      <FormStepContainer
        title={t("dashboard", "liveFormEditor.stepPrizes.title")}
        description={t("dashboard", "liveFormEditor.stepPrizes.description")}
        data-test="prizes-step-live-form-editor"
        form={form}
        actionsProps={{ onBack, onNext, disableNext: isImageUploading, disableBack: isImageUploading }}
      >
        <Box>
          {isSmallScreen ? (
            list
          ) : (
            <DndContext collisionDetection={closestCenter} onDragEnd={prizeList.onDragEnd}>
              <SortableContext items={prizes.map((prize) => prize.id)} strategy={verticalListSortingStrategy}>
                {list}
              </SortableContext>
            </DndContext>
          )}
          <Stack
            direction="row"
            justifyContent={isSmallScreen ? "center" : "flex-start"}
            marginTop={prizes.length > 0 ? 1.5 : 0}
          >
            <DashboardButton
              variant="outlined"
              vibe="brand"
              onClick={() => addPrize()}
              startIcon={<Add />}
              data-test="add-prize-button"
            >
              {t(
                "dashboard",
                prizes.length > 1 ? "liveFormEditor.stepPrizes.addAnotherPrize" : "liveFormEditor.stepPrizes.addPrize"
              )}
            </DashboardButton>
          </Stack>
        </Box>
        <PrizeValueLimitDialog
          restriction={valueRestriction}
          onEditPrizes={onEditPrizes}
          onBackToDashboard={onBackToDashboard}
        />
      </FormStepContainer>
    </FormProvider>
  );
};
